// src/pages/public/TermsPage.js — Terms of Service, "Last updated: November 3, 2026"
// (2026-11-03, the owner's call, no counsel: §7 "Promo codes" — a code lowers the
// first month only, then the regular price renews; it can never make month one free,
// because buying the pass is the parent's consent and must charge at least $0.50
// (polyp-roles' consent floor). The October 29 text is legal-archive/terms-2026-10-29.md;
// this one is terms-2026-11-03.md.)
// (2026-10-29, the owner's approval, no counsel: §7 rewritten for the Season Pass as a
// monthly subscription — what it is, who buys it, US-only through Stripe, renewal on
// the purchase day, cancel from My children → Manage subscription, no refunds except a
// refused payment, taxes shown at checkout — the dormant organization-collects-payments
// sentence deleted; §8 the App Review 1.2 sentence (reports, blocks, removal within
// 24 hours); §10 a real content licence (embeds, previews, watermark, re-titling, the
// card-photo cut-out), uploader responsibility and a copyright-complaints route; §4
// verified phone, one account, @handles are not property; §5 Organizations own their
// data, deletion archives; §11 survival; §13 a liability cap; §15 venue (Pinellas
// County, small-claims carve-out, no arbitration); §16 phone + "a Florida limited
// liability company"; §1 the Apple EULA sentence and one definition of "Service"; new
// §17 Indemnity and §18 General. The October 28 text is legal-archive/terms-2026-10-28.md.)
// (2026-10-28, wording only: §7 says a withdrawn/deleted child's Season Pass is
// cancelled at period end with no refund; the October 26 text is
// legal-archive/terms-2026-10-26.md)
// (age gate + under-13 parental consent, guardian card photos, Season Pass). Routed
// at /terms since 2026-10-26 (it was TermsPageV2.js, unrouted, from 2026-09-28); the
// September 23 text it replaced is legal-archive/terms-2026-09-23.md. Signed off by
// the owner without counsel on 2026-10-26. A text change is a new version: bump
// "Last updated" and archive the previous text verbatim (legal-archive/README.md).
// This page does NOT bump the fan Terms version (the four copies hold "2026-10-26" —
// the rule is beside TOS_VERSION in web/playerwiki/web/js/claim.js: a wording-only
// re-date moves nothing; the four copies move together only with a build that can
// carry the new string).
import React from "react";
import { Link } from "react-router-dom";
import usePlayerWikiTab from "./usePlayerWikiTab";
import "./LegalPage.css";

export default function TermsPage() {
  usePlayerWikiTab("Terms of Service — PlayerWiki");   // title + PlayerWiki tab icon (the console's say Polyp)
  return (
    <div className="legal-bg">
      <div className="legal-card">
        {/* Page chrome, not policy text (legal-archive/extract skips .legal-brand):
            PlayerWiki, back to playerwiki.org, since 2026-10-28 — both sites' Terms /
            Privacy links land here, and "Polyp" read as another company. */}
        <a href="https://playerwiki.org" className="legal-brand">Player<span className="legal-brand-wiki">Wiki</span></a>

        <h1 className="legal-title">Terms of Service</h1>
                <p className="legal-updated">Last updated: November 3, 2026</p>

        <div className="legal-body">
          <section>
            <h2>1. Agreement to Terms</h2>
            <p>
              By accessing or using the Service described in Section 2, operated by Polyp LLC, a
              Florida limited liability company ("Polyp", "we", "us", "our"), you agree to be bound
              by these Terms of Service. If you do not agree, do not use the Service.
            </p>
            <p>
              If you use our iOS apps, Apple's Licensed Application End User License Agreement also
              applies to the app itself; these Terms govern the Service.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              Polyp LLC operates two free products for youth basketball. PlayerWiki Manager is a
              league-management and live-scorekeeping tool for leagues, schools, clubs and tournaments:
              the PlayerWiki Manager iOS app, the web console at app.polyp.me, and the APIs behind them.
              PlayerWiki is a public, searchable wiki of youth basketball players, teams and game
              results at playerwiki.org: the website, the PlayerWiki iOS app, and the APIs behind them.
              Games kept in PlayerWiki Manager feed PlayerWiki. Together these are the "Service".
            </p>
          </section>

          <section>
            <h2>3. Eligibility</h2>
            <p>
              You must be at least 13 years old to create an account. A child under 13 may use PlayerWiki
              only through an account that their parent or legal guardian has approved by giving verified
              parental consent, as described in our Privacy Policy.
            </p>
            <p>
              If you are under 18, you may use the Service only with the permission of a parent or legal
              guardian. Your parent or guardian agrees to these Terms on your behalf and is responsible for
              your use of the Service.
            </p>
            <p>
              You must be at least 18 years old to use PlayerWiki Manager, to act for an Organization
              (Section 5), or to approve a child's account as their parent or guardian.
            </p>
            <p>
              By using the Service, you represent that you meet these requirements.
            </p>
          </section>

          <section>
            <h2>4. Account Registration</h2>
            <p>
              To use certain features, you must create an account. Accounts need a phone number that
              you verify by text message, except a child's account that a parent approved, which may
              use Apple or Google sign-in instead. You agree to provide accurate, current, and complete
              information and to keep it updated. You are responsible for maintaining the
              confidentiality of your account credentials and for all activity under your account.
            </p>
            <p>
              Keep to one account per person. An @handle is not your property: we may reclaim or
              rename a handle that breaks these Terms or impersonates someone.
            </p>
          </section>

          <section>
            <h2>5. Organization Accounts</h2>
            <p>
              If you use PlayerWiki Manager on behalf of a league, school, club, team or tournament (an
              "Organization"), you represent that you have the authority to act for it. Organizations
              are responsible for the accuracy of the schedules, rosters and results they enter, for
              the staff they invite, and for their communications with players, guardians and officials
              through the Service. PlayerWiki Manager is free; Polyp does not charge Organizations a
              subscription fee.
            </p>
            <p>
              Rosters entered in PlayerWiki Manager are shown publicly on PlayerWiki, including each
              player's full name, jersey number, position, team and statistics, as described in our
              Privacy Policy. By adding a player to a roster, you represent that you are authorized to
              share that player's information and to have it shown publicly on PlayerWiki, and that you
              have obtained any parental or guardian consent your program or local law requires.
            </p>
            <p>
              Your Organization owns and controls the schedules, rosters and results it enters. Polyp
              processes them to run the Service and publishes player names and statistics on PlayerWiki
              as our Privacy Policy describes; officials and scorekeepers record games under the league's
              authority. If an Organization account is deleted, its records are archived rather than
              erased: results already published stay on PlayerWiki, and a player's page stays up unless
              it is hidden or removed as our Privacy Policy describes.
            </p>
          </section>

          <section>
            <h2>6. Fan and Guardian Accounts</h2>
            <p>
              If you use PlayerWiki as a fan or as a player's parent or guardian, you may follow players
              and teams, claim the player card of a player you are responsible for, and post highlight
              clips. Player information shown on PlayerWiki comes from games recorded by Organizations
              in PlayerWiki Manager; Polyp is not a party to your relationship with any Organization. How a
              player card is claimed, corrected or removed is described in our Privacy Policy. A parent
              or guardian can have any clip or photo of their child removed, and can have their child's
              name hidden; our Privacy Policy, Sections 5 and 7, explain how.
            </p>
            <p>
              <strong>Card photos.</strong> If you claimed a player's card as their parent or guardian,
              you may send us a photo of that child for the card. By sending one, you confirm that you
              are the child's parent or legal guardian and have the right to share the photo, and you
              allow us to edit it (for example, to remove the background and fit it to the card) and to
              show it publicly on the card and wherever the card is displayed or shared. We may decline
              or remove any photo, and you can remove it at any time. Our Privacy Policy explains how we
              handle card photos.
            </p>
          </section>

          <section>
            <h2>7. Payments and the Season Pass</h2>
            <p>
              PlayerWiki Manager and PlayerWiki are free to use. The Season Pass is an optional paid
              subscription; you never need it to use either product.
            </p>
            <p>
              <strong>What the Season Pass is.</strong> A parent or guardian who is 18 or older buys the
              Season Pass for a child's claimed player card. It lets that card earn the month's Locker
              rewards by completing objectives, and rewards a card has earned are kept after the pass
              ends. Rewards are digital items for the card: they have no cash value, cannot be
              transferred or exchanged, and we may change each month's objectives and rewards. The
              Season Pass is sold only in the United States, through our payment processor Stripe,
              not through Apple; Apple is not a party to the purchase.
            </p>
            <p>
              <strong>Price and renewal.</strong> The price and billing period are shown at checkout
              before you buy. Prices shown at checkout are the amount charged; if tax is ever added, it
              will be shown before you pay. The Season Pass is a monthly subscription: it renews
              automatically each month on the day you bought it, at the price shown at checkout, until
              you cancel it.
            </p>
            <p>
              <strong>Promo codes.</strong> A promo code can lower the price of the first month of a
              new Season Pass. The first month's price and the regular monthly price that follows are
              shown before you pay; from the second month the Season Pass renews at the regular price
              until you cancel. A promo code cannot make the first month free: when buying the Season
              Pass also approves a child's account, it always charges at least $0.50. A promo code has
              no cash value, applies once per Season Pass, and we may change or end it at any time.
            </p>
            <p>
              <strong>Cancelling.</strong> You can cancel at any time from My children → Manage
              subscription, in the PlayerWiki app or on playerwiki.org. Cancelling stops future charges;
              the pass stays active until the end of the month already paid for.
            </p>
            <p>
              <strong>Refunds.</strong> Apart from a payment we refuse (for example, one made with a
              prepaid card, which is refunded automatically), Season Pass payments are not refundable,
              including for a month you cancel part-way through, except where the law requires a refund.
            </p>
            <p>
              <strong>The Season Pass and parental consent.</strong> A parent or guardian approves a
              child's account (Section 3) either by buying the Season Pass or, for free, by verifying
              their identity with a photo ID (our Privacy Policy, Section 6). If a Season Pass is later
              cancelled or lapses, that approval is not withdrawn and the child's account is not deleted.
              If a parent withdraws consent, the child's account is deleted, or a Season Pass a parent
              started expires before the child joins, the Season Pass is cancelled at the end of the
              billing period already paid for and the remainder of that period is not refunded.
            </p>
            <p>
              Payments are processed by Stripe; by paying you also agree to Stripe's terms of service.
            </p>
          </section>

          <section>
            <h2>8. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose</li>
              <li>Transmit harmful, threatening, abusive, or harassing content</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Use the Service to send unsolicited communications (spam)</li>
              <li>Impersonate any person or entity</li>
              <li>Scrape, crawl, or use automated tools to extract data from the Service</li>
            </ul>
            <p>
              Objectionable content and abusive users are not tolerated. Anyone can report a clip, and
              signed-in fans can block an uploader. We review reports and remove content that breaks
              these rules, and may remove the account that posted it, within 24 hours of a report.
            </p>
          </section>

          <section>
            <h2>9. Messaging &amp; Communications</h2>
            <p>
              The Service sends transactional messages: SMS staff invitations and player-card claim
              links sent by Organizations through PlayerWiki Manager, officiating-assignment notices,
              game and follow notifications from PlayerWiki — app notifications, or text messages for a fan
              who follows a team but has no PlayerWiki app installed — that you can manage in the app,
              and the parent-consent and join texts described in our Privacy Policy, Section 13.
              Organizations are responsible for ensuring
              their communications comply with applicable laws, including the Telephone Consumer
              Protection Act (TCPA) for SMS messages. By providing your phone number, you consent to
              receive these transactional messages. You may opt out at any time in the app's
              notification settings or by replying STOP.
            </p>
          </section>

          <section>
            <h2>10. Intellectual Property and Your Content</h2>
            <p>
              The Service and its original content, features, and functionality are owned by Polyp LLC
              and are protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p>
              <strong>Your content.</strong> You keep ownership of the clips, photos and other content
              you post. You give Polyp a worldwide, royalty-free licence to host, store, copy, edit
              (including re-titling a clip, cutting out a card photo and adding a watermark), display,
              distribute, and let others view and share that content on PlayerWiki, in link previews,
              and in embeds on other websites, for as long as it is on the Service and as our Privacy
              Policy describes. You confirm that you have the right to post it, including for anyone
              shown in it, and that it complies with your venue's rules and with local recording law.
            </p>
            <p>
              <strong>Copyright complaints.</strong> If you believe a clip or other content on the
              Service infringes your copyright, email{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a> with a link to it and a description of
              your work. We remove infringing content, and we may close the accounts of repeat
              infringers.
            </p>
          </section>

          <section>
            <h2>11. Termination</h2>
            <p>
              We may suspend or terminate your account at any time for violations of these Terms or for
              any reason at our discretion. You may delete your account at any time. Upon termination,
              your right to use the Service ceases immediately.
            </p>
            <p>
              These parts of our agreement survive termination or deletion of your account: the content
              licence in Section 10, the record of a parent's consent described in our Privacy Policy,
              the payment terms in Section 7, and Sections 13 (Limitation of Liability), 15 (Governing
              Law and Disputes), 17 (Indemnity) and 18 (General Terms).
            </p>
          </section>

          <section>
            <h2>12. Disclaimers</h2>
            <p>
              The Service is provided "as is" and "as available" without warranties of any kind, either
              express or implied. We do not guarantee that the Service will be uninterrupted, secure, or
              error-free. We are not responsible for the actions, content, or practices of
              Organizations, fans or other users of the Service.
            </p>
          </section>

          <section>
            <h2>13. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Polyp LLC shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, or any loss of profits or
              revenues, whether incurred directly or indirectly, arising from your use of the Service.
            </p>
            <p>
              To the fullest extent permitted by law, our total liability to you for all claims arising
              from the Service will not exceed the greater of $100 or the amount you paid us in the 12
              months before the claim. Some jurisdictions do not allow these limits, so they may not
              apply to you.
            </p>
          </section>

          <section>
            <h2>14. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of material changes by
              posting the updated Terms on the Service. Your continued use after changes constitutes
              acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2>15. Governing Law and Disputes</h2>
            <p>
              These Terms are governed by the laws of the State of Florida, United States, without
              regard to conflict of law provisions.
            </p>
            <p>
              If a dispute cannot be resolved by writing to us, it will be brought in the state or
              federal courts located in Pinellas County, Florida, and you and Polyp consent to their
              jurisdiction. Either of us may instead bring an individual claim in small claims court
              where it qualifies.
            </p>
          </section>

          <section>
            <h2>16. Contact</h2>
            <p>
              If you have questions about these Terms, contact us at{" "}
              <a href="mailto:help@polyp.me">help@polyp.me</a> or by phone at{" "}
              <a href="tel:3058786594">305-878-6594</a>.
            </p>
            {/* The postal address, supplied by the owner (2026-10-23). */}
            <p>
              Polyp LLC, a Florida limited liability company, 7901 4th St N, Ste 300, St. Petersburg,
              FL 33702.
            </p>
          </section>

          <section>
            <h2>17. Indemnity</h2>
            <p>
              If you are an Organization, or you post clips, photos or other content, you agree to
              defend Polyp LLC and its members, managers and staff against claims, and to cover the
              damages and reasonable legal costs that result, arising from the content you post, the
              rosters, results and other information you enter, or your breach of these Terms —
              including a claim that you did not have the right to publish a player's information, or
              to film or post someone.
            </p>
          </section>

          <section>
            <h2>18. General Terms</h2>
            <p>
              These Terms, together with our Privacy Policy, are the entire agreement between you and
              Polyp about the Service. If any part of these Terms is found unenforceable, the rest stays
              in effect. If we do not enforce a term, that is not a waiver of our right to enforce it
              later. We may assign these Terms to a successor to our business; you may not assign them.
            </p>
          </section>
        </div>

        <div className="legal-footer">
          <Link to="/privacy">Privacy Policy</Link>
          <span className="legal-footer-sep">&middot;</span>
          <a href="https://playerwiki.org">Back to PlayerWiki</a>
        </div>
      </div>
    </div>
  );
}
